<template>
  <div>
    <div class="space-y-4">
      <div class="flex flex-wrap space-x-2 gap-2">
        <div
          v-for="stip in step?.stips"
          :key="stip.id"
          class="rounded-md border-2 border-disabled px-4 py-1 text-headline flex justify-between space-x-2"
        >
          <div>
            {{ stip.name }}
            <span v-if="stip.count > 1">
              ({{ stip.count }} {{ $t("COMMON.REQUIRED") }})
            </span>
          </div>
          <button
            data-cy="chat-close-button"
            class="text-gray-500 hover:text-gray-700"
            @click="removeStipFromStep(stip.id)"
          >
            <icon-x />
          </button>
        </div>
      </div>
      <div v-if="!step?.stips?.length">
        {{ $t("CHECKOUT.DOCUMENTS_STEP_NO_DOCUMENTS_WARNING") }}
      </div>
      <div class="flex items-center space-x-2 font-semibold text-primary">
        <icon-base :icon="IconPlusSmall" class="relative -top-px" />
        <button @click="showStipsModal = true">
          {{ $t("DEALS.DOCUMENTS.ADD_DOCUMENTS") }}
        </button>
      </div>
    </div>
    <modal v-if="showStipsModal" :title="''" xl :close="handleModalClose">
      <template #content>
        <stips v-model="selectedStips" class="px-6 pb-6" />
        <div class="flex justify-end border-t border-gray-200 p-6">
          <modal-buttons
            class="bg-white"
            :on-save="handleModalSave"
            :on-cancel="handleModalClose"
          />
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { storeToRefs } from "pinia";

import { checkoutBaseToDedicatedStep } from "@/helpers/checkout";
import { useCheckoutStore } from "@/stores/checkout";

import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Modal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import Stips from "@/views/deals/components/Stips.vue";

import type { FieldStip } from "@/models/stips";

const selectedStips = ref<FieldStip[]>([]);

const showStipsModal = ref(false);

const { selectedStep } = storeToRefs(useCheckoutStore());

const step = computed(() =>
  checkoutBaseToDedicatedStep(selectedStep.value, "documents")
);

const removeStipFromStep = (stipId: FieldStip["id"]) => {
  if (!step.value) {
    return;
  }

  step.value.stips = step.value?.stips.filter((stip) => stip.id !== stipId);
};

const handleModalSave = async () => {
  if (!step.value) {
    return;
  }

  step.value.stips = selectedStips.value;

  showStipsModal.value = false;
};

const handleModalClose = () => {
  if (!step.value) {
    return;
  }

  selectedStips.value = step.value.stips;

  showStipsModal.value = false;
};

watch(
  () => step.value?.stips,
  (newStips) => {
    if (!newStips) {
      return;
    }

    selectedStips.value = newStips;
  },
  { immediate: true }
);
</script>
